/**
 * ChannelMentionAutocomplete Component
 * Dropdown for #channel reference selection with search filtering
 * Triggered when user types # in MessageInput
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { Channel } from '@/types/api.types';
import { Hash, Lock, X } from 'lucide-react';

export interface ChannelSelection {
  channelId: string;
  name: string;
}

interface ChannelMentionAutocompleteProps {
  channels: Channel[];
  query: string;
  onSelect: (channel: ChannelSelection) => void;
  onClose: () => void;
  position?: { top: number; left: number };
}

export const ChannelMentionAutocomplete: React.FC<ChannelMentionAutocompleteProps> = ({
  channels,
  query,
  onSelect,
  onClose,
  position,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [prevQuery, setPrevQuery] = useState(query);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter channels based on query (archived channels are excluded by the caller)
  const searchText = query.toLowerCase();
  const filteredChannels = channels.filter((channel) =>
    channel.name.toLowerCase().includes(searchText)
  );

  // Reset selected index when the query changes (adjust state during render)
  if (prevQuery !== query) {
    setPrevQuery(query);
    setSelectedIndex(0);
  }

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (filteredChannels.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, filteredChannels.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
        case 'Tab': {
          e.preventDefault();
          const channel = filteredChannels[selectedIndex];
          if (channel) {
            onSelect({ channelId: channel.id, name: channel.name });
          }
          break;
        }
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [filteredChannels, selectedIndex, onSelect, onClose]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if (filteredChannels.length === 0) {
    return (
      <div
        ref={dropdownRef}
        className="absolute z-50 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3"
        style={position}
      >
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <span>Няма намерени канали</span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={dropdownRef}
      className="absolute z-50 w-80 max-h-64 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
      style={position}
    >
      <div className="p-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <Hash className="w-4 h-4" />
          <span>Препратка към канал</span>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="py-1" role="listbox" aria-label="Канали">
        {filteredChannels.map((channel, index) => (
          <button
            key={channel.id}
            role="option"
            id={`channel-option-${index}`}
            aria-selected={index === selectedIndex}
            onClick={() => onSelect({ channelId: channel.id, name: channel.name })}
            className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
              index === selectedIndex
                ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500'
                : ''
            }`}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            {/* Icon */}
            <div className="w-8 h-8 rounded-full bg-blue-500/15 flex items-center justify-center text-blue-500 flex-shrink-0">
              {channel.type === 'private' ? <Lock className="w-4 h-4" /> : <Hash className="w-4 h-4" />}
            </div>

            {/* Channel info */}
            <div className="flex-1 text-left overflow-hidden">
              <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {channel.name}
              </div>
              {channel.description && (
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {channel.description}
                </div>
              )}
            </div>

            {/* Selected indicator */}
            {index === selectedIndex && (
              <div className="text-blue-500">
                <Hash className="w-4 h-4" />
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="p-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center justify-between">
          <span>↑↓ Navigate</span>
          <span>↵ Select</span>
          <span>Esc Close</span>
        </div>
      </div>
    </div>
  );
};
